import { useCallback, useEffect, useState } from "react";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  defaultBabyEventForm,
  deleteBabyEvent,
  eventCategoryLabels,
  eventTypeOptionsForCategory,
  feedingSides,
  feedingUnitForType,
  fetchBabyEvents,
  saveBabyEvent,
  sleepEventDisplay,
} from "./BabyTrackerShared";

const BabyTrackerEvents = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(defaultBabyEventForm());
  const [editingId, setEditingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const refreshEvents = useCallback(() => {
    fetchBabyEvents(categoryFilter).then((data) => setEvents(data || []));
  }, [categoryFilter]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((currentValue) => {
      if (name === "eventCategory") {
        const nextType = eventTypeOptionsForCategory(value)[0];
        return {
          ...currentValue,
          eventCategory: value,
          eventType: nextType,
          feedingAmount: value === "feeding" ? currentValue.feedingAmount : "",
          feedingSide: value === "feeding" && nextType === "Breast" ? currentValue.feedingSide : "",
          feedingUnit: value === "feeding" ? feedingUnitForType(nextType) : "",
          endedAt: value === "sleep" ? "" : currentValue.endedAt,
        };
      }
      if (name === "eventType" && currentValue.eventCategory === "feeding") {
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: feedingUnitForType(value),
          feedingSide: value === "Breast" ? currentValue.feedingSide : "",
        };
      }
      return { ...currentValue, [name]: value };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const eventForm = {
      ...form,
      endedAt: form.eventCategory === "sleep" ? "" : form.endedAt,
      feedingUnit: form.eventCategory === "feeding" ? feedingUnitForType(form.eventType) : "",
      feedingSide: form.eventCategory === "feeding" && form.eventType === "Breast" ? form.feedingSide : "",
    };
    saveBabyEvent(eventForm, editingId)
      .then(() => {
        setStatusMessage(editingId ? "Baby event updated." : "Baby event logged.");
        setEditingId(null);
        setForm(defaultBabyEventForm());
        refreshEvents();
      })
      .catch(() => {
        setStatusMessage("Unable to save baby event.");
      });
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setStatusMessage("");
    setForm({
      eventCategory: entry.eventCategory,
      eventType: entry.eventType,
      occurredAt: entry.occurredAtInput,
      endedAt: entry.endedAtInput || "",
      feedingAmount: entry.feedingAmount ?? "",
      feedingUnit: entry.feedingUnit || feedingUnitForType(entry.eventType),
      feedingSide: entry.eventType === "Breast" ? entry.feedingSide || "" : "",
      notes: entry.notes || "",
    });
  };

  const removeEvent = (eventId) => {
    deleteBabyEvent(eventId).then(() => {
      setStatusMessage("Baby event deleted.");
      setEditingId(null);
      setForm(defaultBabyEventForm());
      refreshEvents();
    });
  };

  const typeOptions = eventTypeOptionsForCategory(form.eventCategory);
  const showFeedingFields = form.eventCategory === "feeding";
  const isBreastFeeding = showFeedingFields && form.eventType === "Breast";

  return (
    <div className="BabyTrackerPage">
      <div className="BabyTrackerGrid">
        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          <h2 className="BabyTrackerTitle">{editingId ? "Edit baby event" : "Add baby event"}</h2>
          <p className="BabyTrackerSubtitle">
            Track diapers, feedings, and sleep or wake events.
          </p>
          {statusMessage ? <p className="BabyTrackerStatus">{statusMessage}</p> : null}
          <form onSubmit={handleSubmit}>
            <div className="BabyTrackerTwoColumn">
              <div className="inputDiv">
                <label>Category</label>
                <select
                  className="inputBox addInput"
                  name="eventCategory"
                  value={form.eventCategory}
                  onChange={handleChange}
                >
                  <option value="diaper">Diaper</option>
                  <option value="feeding">Feeding</option>
                  <option value="sleep">Sleep</option>
                </select>
              </div>
              <div className="inputDiv">
                <label>Type</label>
                <select
                  className="inputBox addInput"
                  name="eventType"
                  value={form.eventType}
                  onChange={handleChange}
                >
                  {typeOptions.map((typeValue) => (
                    <option key={typeValue} value={typeValue}>
                      {typeValue}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="BabyTrackerTwoColumn">
              <div className="inputDiv">
                <label>Occurred</label>
                <input
                  type="datetime-local"
                  className="inputBox addInput"
                  name="occurredAt"
                  value={form.occurredAt}
                  onChange={handleChange}
                />
              </div>
              {form.eventCategory === "sleep" ? null : (
                <div className="inputDiv">
                  <label>Ended</label>
                  <input
                    type="datetime-local"
                    className="inputBox addInput"
                    name="endedAt"
                    value={form.endedAt}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>

            {showFeedingFields ? (
              <div className="BabyTrackerTwoColumn">
                <div className="inputDiv">
                  <label>{isBreastFeeding ? "Time (minutes)" : "Amount (oz)"}</label>
                  <input
                    className="inputBox addInput"
                    name="feedingAmount"
                    value={form.feedingAmount}
                    onChange={handleChange}
                    inputMode="decimal"
                  />
                </div>
                {isBreastFeeding ? (
                  <div className="inputDiv">
                    <label>Started side</label>
                    <select
                      className="inputBox addInput"
                      name="feedingSide"
                      value={form.feedingSide}
                      onChange={handleChange}
                    >
                      {feedingSides.map((sideValue) => (
                        <option key={sideValue || "none"} value={sideValue}>
                          {sideValue || "N/A"}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="inputDiv">
              <label>Notes</label>
              <textarea
                className="inputBox textAreaAdd BabyTrackerTextarea"
                name="notes"
                value={form.notes}
                onChange={handleChange}
              />
            </div>

            <div className="BabyTrackerActionRow">
              <button type="submit" className="summaryButton">
                {editingId ? "Save" : "Add"}
              </button>
              {editingId ? (
                <button
                  type="button"
                  className="summaryButton"
                  onClick={() => {
                    setEditingId(null);
                    setForm(defaultBabyEventForm());
                  }}
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </form>
        </div>

        <div className="shoppingListDiv BabyTrackerPanel">
          <div className="BabyTrackerHistoryHeader">
            <h2 className="BabyTrackerTitle">Event log</h2>
            <select
              className="inputBox BabyTrackerFilter"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="">All</option>
              <option value="diaper">Diapers</option>
              <option value="feeding">Feedings</option>
              <option value="sleep">Sleep</option>
            </select>
          </div>
          {events.length ? (
            events.map((entry) => {
              const sleepDisplay = entry.eventCategory === "sleep" ? sleepEventDisplay(entry.eventType) : null;
              return (
                <div className={sleepDisplay ? "BabyTrackerHistoryCard BabyTrackerSleepCard" : "BabyTrackerHistoryCard"} key={entry.id}>
                  {sleepDisplay ? (
                    <div className="BabyTrackerSleepHeader">
                      <span className={`BabyTrackerSleepBadge BabyTrackerSleepBadge-${sleepDisplay.tone}`}>
                        {entry.eventType}
                      </span>
                      <div>
                        <strong>{sleepDisplay.label}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="BabyTrackerHistoryRow">
                      <div>
                        <strong>{entry.eventType}</strong>
                        <div>{eventCategoryLabels[entry.eventCategory]}</div>
                      </div>
                      <span>{entry.occurredAt}</span>
                    </div>
                  )}
                  {entry.feedingAmount ? (
                    <div className="BabyTrackerHistoryMeta">
                      <span>
                        {entry.feedingAmount} {entry.feedingUnit}
                      </span>
                      {entry.feedingSide ? <span>{entry.feedingSide}</span> : null}
                    </div>
                  ) : null}
                  {entry.notes ? <p className="BabyTrackerNotes">{entry.notes}</p> : null}
                  <div className="BabyTrackerActionRow">
                    <button className="summaryButton" onClick={() => beginEdit(entry)}>
                      Edit
                    </button>
                    <button className="summaryButton" onClick={() => removeEvent(entry.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No events logged yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BabyTrackerEvents;
